/**
 * Admin LLM Keys
 * ==============
 * Platform admin console for managing per-provider LLM API keys (OpenAI,
 * Anthropic, Google, Grok). Keys are stored encrypted at rest in the
 * `platform_llm_config` collection. Only the last 4 characters surface in the
 * UI; full keys are never returned by the GET endpoint.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { CircleNotch, CheckCircle, Warning, Plug, Key, TestTube } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { adminApiGet, adminApiPost, adminApiDelete } from '../../utils/adminApi';

const PROVIDERS = [
  { id: 'openai', label: 'OpenAI', help: 'sk-... (used by gpt-4o-mini, gpt-4o, gpt-5.2)' },
  { id: 'anthropic', label: 'Anthropic', help: 'sk-ant-... (used by Claude Haiku/Sonnet/Opus 4.5)' },
  { id: 'google', label: 'Google', help: 'AI... (used by Gemini 3 Flash/Pro)' },
  { id: 'grok', label: 'xAI (Grok)', help: 'xai-... (used by Grok 4)' },
];

export default function AdminLLMKeys() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // provider_id while editing
  const [keyValue, setKeyValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApiGet('/api/admin/llm-keys');
      setProviders(res?.providers || []);
    } catch (e) {
      toast.error('Could not load LLM keys');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (provider) => {
    if (!keyValue || keyValue.length < 10) {
      toast.error('API key must be at least 10 characters');
      return;
    }
    setSaving(true);
    try {
      await adminApiPost('/api/admin/llm-keys', { provider, api_key: keyValue });
      toast.success(`${provider} key saved`);
      setEditing(null);
      setKeyValue('');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Could not save key');
    }
    setSaving(false);
  };

  const deactivate = async (provider) => {
    if (!window.confirm(`Deactivate ${provider} key? Functions using this provider will fail until a new key is added.`)) return;
    try {
      await adminApiDelete(`/api/admin/llm-keys/${provider}`);
      toast.success(`${provider} key deactivated`);
      await load();
    } catch (e) {
      toast.error('Could not deactivate key');
    }
  };

  const testProvider = async (provider) => {
    setTesting(provider);
    try {
      const res = await adminApiPost(`/api/admin/llm-keys/test/${provider}`, {});
      if (res?.ok) {
        toast.success(`${provider} key works ✓ (${res.latency_ms}ms)`);
      } else {
        toast.error(`${provider} test failed: ${res?.error || 'unknown error'}`);
      }
    } catch (e) {
      toast.error(e?.message || `${provider} test failed`);
    }
    setTesting(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch size={28} className="text-[#6366F1] animate-spin" />
      </div>
    );
  }

  const configuredMap = Object.fromEntries(providers.map(p => [p.provider, p]));

  return (
    <div className="space-y-6" data-testid="admin-llm-keys">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Key size={24} className="text-[#6366F1]" />
          <h1 className="text-2xl font-semibold text-white">Platform LLM Keys</h1>
        </div>
        <p className="text-sm text-[#A0AEC0] max-w-2xl">
          Configure API keys for each LLM provider. The platform will route worker-build, intent-parse and other platform functions through these keys.
          Users can pick any model whose provider is configured here. Keys are encrypted at rest and never returned in full.
        </p>
      </div>

      <div className="space-y-3">
        {PROVIDERS.map((meta) => {
          const cfg = configuredMap[meta.id];
          const isConfigured = cfg?.configured;
          const isEditing = editing === meta.id;
          return (
            <div
              key={meta.id}
              data-testid={`provider-card-${meta.id}`}
              className="bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] rounded-xl p-5"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`mt-1 p-2 rounded-lg ${isConfigured ? 'bg-[rgba(74,222,128,0.10)]' : 'bg-[rgba(255,255,255,0.04)]'}`}>
                    {isConfigured ? (
                      <CheckCircle size={20} weight="fill" className="text-[#4ADE80]" />
                    ) : (
                      <Warning size={20} className="text-[#FBBF24]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-semibold text-white">{meta.label}</h3>
                      {isConfigured && cfg.key_last_4 && (
                        <span className="text-xs text-[#A0AEC0] font-mono">
                          ····{cfg.key_last_4}
                        </span>
                      )}
                      {isConfigured && cfg.source === 'env' && (
                        <span
                          className="text-[9px] font-bold tracking-[0.08em] px-1.5 py-0.5 rounded bg-[rgba(251,191,36,0.10)] text-[#FBBF24]"
                          title="Inherited from environment variable. Add a managed key to take control."
                        >
                          ENV-MANAGED
                        </span>
                      )}
                      {isConfigured && cfg.source === 'db' && (
                        <span className="text-[9px] font-bold tracking-[0.08em] px-1.5 py-0.5 rounded bg-[rgba(74,222,128,0.10)] text-[#4ADE80]">
                          MANAGED
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#A0AEC0] mt-0.5">{meta.help}</p>
                    {isConfigured && cfg.updated_at && (
                      <p className="text-[10px] text-[#718096] mt-1">
                        Updated {new Date(cfg.updated_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isConfigured && (
                    <button
                      onClick={() => testProvider(meta.id)}
                      disabled={testing === meta.id}
                      data-testid={`test-${meta.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#A0AEC0] border border-[rgba(255,255,255,0.1)] rounded-lg hover:text-white hover:border-[rgba(255,255,255,0.25)] disabled:opacity-50"
                    >
                      {testing === meta.id ? <CircleNotch size={12} className="animate-spin" /> : <TestTube size={12} />}
                      Test
                    </button>
                  )}
                  <button
                    onClick={() => { setEditing(meta.id); setKeyValue(''); }}
                    data-testid={`edit-${meta.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#6366F1] text-white rounded-lg hover:bg-[#5558D9]"
                  >
                    <Plug size={12} />
                    {isConfigured ? 'Rotate' : 'Add key'}
                  </button>
                  {isConfigured && (
                    <button
                      onClick={() => deactivate(meta.id)}
                      data-testid={`deactivate-${meta.id}`}
                      className="px-3 py-1.5 text-xs text-[#F87171] hover:bg-[rgba(248,113,113,0.08)] rounded-lg"
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.06)]">
                  <label className="text-xs text-[#A0AEC0] mb-1.5 block">Paste {meta.label} API key</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="password"
                      value={keyValue}
                      onChange={(e) => setKeyValue(e.target.value)}
                      placeholder={meta.help.split(' ')[0]}
                      data-testid={`input-${meta.id}`}
                      className="flex-1 px-3 py-2 bg-[#0B0C10] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-white font-mono focus:outline-none focus:border-[#6366F1]"
                    />
                    <button
                      onClick={() => save(meta.id)}
                      disabled={saving}
                      data-testid={`save-${meta.id}`}
                      className="px-4 py-2 bg-[#6366F1] text-white text-sm font-semibold rounded-lg hover:bg-[#5558D9] disabled:opacity-50 flex items-center gap-2"
                    >
                      {saving && <CircleNotch size={12} className="animate-spin" />}
                      Save
                    </button>
                    <button
                      onClick={() => { setEditing(null); setKeyValue(''); }}
                      className="px-3 py-2 text-sm text-[#A0AEC0] hover:text-white"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
